import type { ComputedRef, Ref } from 'vue'
import type { CustomerActiveSubscription } from '@entities/customer'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { SubscriptionStatus } from '@brighterly/lib-core-types/subscription/enums'

import { getCustomerSubscriptionUrl } from '@widgets/subscriptions'
import { formatPrice, useDayjs } from '@shared/lib/format'

import { getKidUrl } from './list/customer-links'

const EMPTY = '-'
// codestyle-ignore: date-format-enum — month-day label for the next charge; DateFormat has no month-day member (decided 2026-10-29)
const NEXT_CHARGE_FORMAT = 'MMM DD'
const ELA_CODE = 'ELA'

interface CustomerSubscriptionCardKid {
  id: number
  name: string
  url: string
}

interface CustomerSubscriptionCardRow {
  label: string
  value: string
}

interface UseCustomerSubscriptionCardOptions {
  subscription: ComputedRef<CustomerActiveSubscription>
}

interface CustomerSubscriptionCardReturn {
  refIsOpen: Ref<boolean>
  refSubscriptionUrl: ComputedRef<string>
  refPausedUntil: ComputedRef<string | null>
  refPlannedPause: ComputedRef<string | null>
  refType: ComputedRef<string>
  refKids: ComputedRef<CustomerSubscriptionCardKid[]>
  refPace: ComputedRef<string>
  refDetails: ComputedRef<CustomerSubscriptionCardRow[]>
  toggle: () => void
}

const lessonsText = (count: number | null | undefined, unit: string): string =>
  (count ? `${count} ${unit}` : EMPTY)

/** One purchase card of the General info tab — the legacy CardPlan. */
export const useCustomerSubscriptionCard = ({ subscription }: UseCustomerSubscriptionCardOptions): CustomerSubscriptionCardReturn => {
  const dayjs = useDayjs()

  const refIsOpen = ref<boolean>(false)

  const refSubscriptionUrl = computed<string>(() => {
    const customerId = subscription.value.customer?.id

    return customerId ? getCustomerSubscriptionUrl(customerId, subscription.value.id) : ''
  })

  const formatDate = (value: string): string => dayjs(value).format(DateFormat.FileDate)

  const refPausedUntil = computed<string | null>(() => {
    const { status, paused_to: pausedTo } = subscription.value

    return status === SubscriptionStatus.Paused && pausedTo ? `until ${formatDate(pausedTo)}` : null
  })

  const refPlannedPause = computed<string | null>(() => {
    const { status, paused_from: pausedFrom, paused_to: pausedTo } = subscription.value

    return status !== SubscriptionStatus.Paused && pausedFrom && pausedTo
      ? `Planned pause period: ${formatDate(pausedFrom)} - ${formatDate(pausedTo)}`
      : null
  })

  const refType = computed<string>(() =>
    (subscription.value.disciplines.some(discipline => discipline.code === ELA_CODE) ? 'ELA' : 'Math'))

  const refKids = computed<CustomerSubscriptionCardKid[]>(() =>
    subscription.value.disciplines
      .map(discipline => discipline.kid)
      .filter((kid): kid is NonNullable<typeof kid> => !!kid)
      .filter((kid, index, kids) => kids.findIndex(item => item.id === kid.id) === index)
      .map(kid => ({ id: kid.id, name: kid.name, url: getKidUrl(kid.id) })))

  const refPace = computed<string>(() => {
    const maxPace = subscription.value.pricing.max_pace
    const pace = maxPace ? `${maxPace} lessons per week` : EMPTY

    return subscription.value.is_premium ? `${pace} (premium tutor)` : pace
  })

  const refDetails = computed<CustomerSubscriptionCardRow[]>(() => {
    const { parts, discount, pricing, next_payment_at: nextPaymentAt } = subscription.value
    const discounts: string[] = []

    if (discount) {
      discounts.push(`${discount}% (${formatPrice(pricing.discount_usd ?? 0)})`)
    }

    if (pricing.trial_discount) {
      discounts.push(`${pricing.trial_discount}% (${formatPrice(pricing.trial_discount_usd ?? 0)})`)
    }

    return [
      { label: 'Period', value: parts ? `${parts} ${parts === 1 ? 'month' : 'months'}` : EMPTY },
      { label: 'Amount', value: `$${Math.round((pricing.one_payment * (parts || 1)) / 100)}` },
      { label: 'Discount', value: discounts.join('; ') },
      {
        label: 'Next charge',
        value: nextPaymentAt
          ? `${dayjs(nextPaymentAt).format(NEXT_CHARGE_FORMAT)} for ${formatPrice(pricing.first_payment)}`
          : EMPTY,
      },
      { label: 'Committed', value: lessonsText(pricing.lessons, pricing.lessons === 1 ? 'lesson' : 'lessons') },
      { label: 'Monthly balance', value: lessonsText(pricing.payment_lessons, 'lessons') },
      { label: 'Bonus', value: EMPTY },
    ]
  })

  const toggle = (): void => {
    refIsOpen.value = !refIsOpen.value
  }

  return {
    refIsOpen,
    refSubscriptionUrl,
    refPausedUntil,
    refPlannedPause,
    refType,
    refKids,
    refPace,
    refDetails,
    toggle,
  }
}
